import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

/**
 * Theme breakpoint values, the single source for the MUI theme and for the screen class boundaries.
 * Changing a value shifts every existing responsive `size` in the application.
 */
export const SCREEN_BREAKPOINTS = {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920
} as const;

/**
 * The three screen classes: `"narrow"` below `sm` (600), `"medium"` from `sm` up to below `md` (600 to 959),
 * `"wide"` from `md` (960). The smallest fully usable width is 360, which is `"narrow"`.
 */
export type ScreenClass = "narrow" | "medium" | "wide";

/**
 * Returns the current screen class and re-renders on a width change without a reload (MUI `useMediaQuery`
 * subscribes to the `matchMedia` change events).
 *
 * Prefer the responsive props of the pattern helpers for layout; use this hook only for behaviour a prop cannot
 * express (for example a full-screen dialog). Never remove an action based on this value: an action may move
 * into a menu (the header side menu below `md` and wherever the header bar does not fit), but which actions are
 * reachable depends on the role alone.
 */
export function useScreenClass(): ScreenClass {
    const theme = useTheme();
    const narrow = useMediaQuery(theme.breakpoints.down("sm"));
    const wide = useMediaQuery(theme.breakpoints.up("md"));
    if (narrow) {
        return "narrow";
    }
    return wide ? "wide" : "medium";
}

/**
 * Whether the screen class is compact: `true` for `"narrow"` and `"medium"` (below `md`, 960), `false` for
 * `"wide"`. It is the one boundary shared by the lists that switch to a compact presentation: `DataTable` shows its
 * card list with the sort and filter button row, the two-section overview pages stack their sections, and the
 * table pagination wraps. The header shows its side menu below it as well, and from it wherever its horizontal bar
 * does not fit on one row (`useWideHeaderFit`).
 *
 * @example const compact = isCompactScreen(useScreenClass());
 */
export function isCompactScreen(screenClass: ScreenClass): boolean {
    return screenClass !== "wide";
}

/** The page's existing `Grid2` sizes from `md` upwards, kept unchanged. */
export interface WideGridSize {
    md: number;
    lg?: number;
    xl?: number;
}

/** A `Grid2` `size` value covering every breakpoint. */
export interface ResponsiveGridSize extends WideGridSize {
    xs: number;
    sm: number;
}

/**
 * Builds a `Grid2` `size`: stacked full width on `"narrow"` (`xs` 12), two columns on `"medium"` (`sm` 6), and
 * the given values from `md` upwards unchanged, so the wide layout stays as it was.
 *
 * `Grid2` subtracts only `spacing` / `columnSpacing` from the width of `size`, never a CSS `gap`: below `md` the
 * container needs `columnSpacing` instead of `gap`, otherwise two `sm: 6` items do not fit side by side.
 *
 * @example responsiveGridSize({md: 3, lg: 2}) // {xs: 12, sm: 6, md: 3, lg: 2}
 */
export function responsiveGridSize(wide: WideGridSize): ResponsiveGridSize {
    return { xs: 12, sm: 6, ...wide };
}

/**
 * Builds a `Grid2` `size` that stacks full width wherever {@link isCompactScreen} holds: `xs` and `sm` 12 on
 * `"narrow"` and `"medium"` (below `md`, 960), and the given values from `md` upwards unchanged, so the wide
 * layout stays as it was. Used for the card columns of the company and sign-up forms.
 *
 * @example compactStackedGridSize({md: 4}) // {xs: 12, sm: 12, md: 4}
 */
export function compactStackedGridSize(wide: WideGridSize): ResponsiveGridSize {
    return { xs: 12, sm: 12, ...wide };
}

/** A `Grid2` `size` value for `xs` and `sm`; `md` and above inherit the `sm` value. */
export interface NarrowGridSize {
    xs: number;
    sm: number;
}

/**
 * Builds a `Grid2` `size` that is full width on `"narrow"` (`xs` 12) and the given value from `sm` upwards.
 * It carries no `md` key, so `"medium"` and `"wide"` keep inheriting `sm` as before. Used for the field rows
 * inside the detail page cards.
 *
 * @example narrowStackedGridSize(4) // {xs: 12, sm: 4}
 */
export function narrowStackedGridSize(sm: number): NarrowGridSize {
    return { xs: 12, sm };
}

/** A flex or `Grid2` `direction` value: stacked below `sm`, a row from `sm` upwards. */
export interface ResponsiveDirection {
    xs: "column";
    sm: "row";
}

/**
 * Builds a `direction` for a `Grid2` container or `Stack`: `"column"` on `"narrow"` so the items stack, `"row"`
 * from `"medium"` upwards, which keeps the wide layout unchanged.
 *
 * @example <Grid2 container direction={responsiveDirection()}>
 */
export function responsiveDirection(): ResponsiveDirection {
    return { xs: "column", sm: "row" };
}

/**
 * A responsive `sx` value: one value below `md`, another from `md` upwards. A type alias, not an interface: MUI's
 * `sx` types carry an index signature, which only a type alias satisfies implicitly.
 */
export type CompactValue<T> = {
    xs: T;
    md: T;
};

/**
 * Builds a responsive `sx` value that switches at the {@link isCompactScreen} boundary (`md`, 960): `compact` on
 * `"narrow"` and `"medium"`, `wide` from `md` upwards, so the wide layout keeps its value. It carries no `sm` key.
 *
 * @example sx={{whiteSpace: compactValue("normal", "nowrap")}} // {xs: "normal", md: "nowrap"}
 */
export function compactValue<T>(compact: T, wide: T): CompactValue<T> {
    return { xs: compact, md: wide };
}
